import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/database.generated';
import { activeHighlightTags } from '@/lib/highlightTagColors';

export type DocumentHighlightTagRow =
  Database['public']['Tables']['document_highlight_tags']['Row'];

export type DocumentHighlightTagColorToken = string;

export const documentHighlightTagsKey = (clinicId: string | null | undefined) =>
  ['document-highlight-tags', clinicId ?? null] as const;

export function useDocumentHighlightTags(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: documentHighlightTagsKey(clinicId),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<DocumentHighlightTagRow[]> => {
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from('document_highlight_tags')
        .select('*')
        .eq('clinic_id', clinicId)
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 30_000,
  });
}

export interface DocumentHighlightTagInput {
  name: string;
  color_token: DocumentHighlightTagColorToken;
}

export function useCreateDocumentHighlightTag(clinicId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: DocumentHighlightTagInput): Promise<DocumentHighlightTagRow> => {
      if (!clinicId) throw new Error('No active clinic');
      const current = queryClient.getQueryData<DocumentHighlightTagRow[]>(
        documentHighlightTagsKey(clinicId),
      );
      const nextOrder = (current ?? []).reduce((max, row) => Math.max(max, row.sort_order), 0) + 10;
      const { data, error } = await supabase
        .from('document_highlight_tags')
        .insert({
          clinic_id: clinicId,
          name: input.name.trim(),
          color_token: input.color_token,
          sort_order: nextOrder,
        })
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: documentHighlightTagsKey(clinicId) });
    },
  });
}

export function useUpdateDocumentHighlightTag(clinicId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: Partial<DocumentHighlightTagInput> & {
        id: string;
        is_archived?: boolean;
        sort_order?: number;
      },
    ): Promise<DocumentHighlightTagRow> => {
      const patch: Database['public']['Tables']['document_highlight_tags']['Update'] = {};
      if (input.name != null) patch.name = input.name.trim();
      if (input.color_token != null) patch.color_token = input.color_token;
      if (input.is_archived != null) patch.is_archived = input.is_archived;
      if (input.sort_order != null) patch.sort_order = input.sort_order;
      const { data, error } = await supabase
        .from('document_highlight_tags')
        .update(patch)
        .eq('id', input.id)
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: documentHighlightTagsKey(clinicId) });
    },
  });
}

export function isHighlightTagLinkUnavailable(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const code = 'code' in error && error.code != null ? String(error.code) : '';
  const message = 'message' in error && error.message != null ? String(error.message) : '';
  if (code === '42P01' || code === 'PGRST205') return true;
  return (
    /document_highlight_tag_links/i.test(message) &&
    /does not exist|schema cache|could not find the table/i.test(message)
  );
}

/** Tag ids already stored on these markings. An absent link table yields an empty map. */
export async function loadHighlightTagIds(
  highlightIds: readonly string[],
): Promise<Map<string, string[]>> {
  const map = new Map<string, string[]>();
  if (highlightIds.length === 0) return map;
  const chunkSize = 80;
  for (let index = 0; index < highlightIds.length; index += chunkSize) {
    const slice = highlightIds.slice(index, index + chunkSize);
    const { data, error } = await supabase
      .from('document_highlight_tag_links')
      .select('highlight_id, tag_id')
      .in('highlight_id', slice);
    if (error) {
      if (isHighlightTagLinkUnavailable(error)) return new Map();
      throw error;
    }
    for (const row of data ?? []) {
      const list = map.get(row.highlight_id) ?? [];
      list.push(row.tag_id);
      map.set(row.highlight_id, list);
    }
  }
  return map;
}

export function useSetDocumentHighlightTags() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      highlightId: string;
      documentId: string;
      clinicId: string;
      tagIds: readonly string[];
    }) => {
      const { data, error } = await supabase
        .from('document_highlight_tags')
        .select('id, name, color_token, is_archived, sort_order')
        .eq('clinic_id', input.clinicId);
      if (error) {
        if (isHighlightTagLinkUnavailable(error)) return;
        throw error;
      }
      const active = new Set(activeHighlightTags(data ?? []).map((tag) => tag.id));
      const tagIds = [...new Set(input.tagIds)].filter((id) => active.has(id));
      const { error: deleteError } = await supabase
        .from('document_highlight_tag_links')
        .delete()
        .eq('highlight_id', input.highlightId);
      if (deleteError) {
        if (isHighlightTagLinkUnavailable(deleteError)) return;
        throw deleteError;
      }
      if (tagIds.length === 0) return;
      const { error: insertError } = await supabase.from('document_highlight_tag_links').insert(
        tagIds.map((tagId) => ({
          highlight_id: input.highlightId,
          tag_id: tagId,
          clinic_id: input.clinicId,
        })),
      );
      if (insertError) throw insertError;
    },
    onSuccess: (_result, input) => {
      void queryClient.invalidateQueries({ queryKey: ['document-highlights', input.documentId] });
      void queryClient.invalidateQueries({ queryKey: ['patient-file-highlights'] });
    },
  });
}

export function useReorderDocumentHighlightTags(clinicId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (orders: Array<{ id: string; sort_order: number }>) => {
      for (const row of orders) {
        const { error } = await supabase
          .from('document_highlight_tags')
          .update({ sort_order: row.sort_order })
          .eq('id', row.id);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: documentHighlightTagsKey(clinicId) });
    },
  });
}
